import React from 'react';
import { Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtDateTime } from '@/lib/format-date';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from '@/components/ui/toast';
import { useConfirm } from '@/hooks/use-confirm';
import { DeveloperRequestsPanel } from './developer-requests';

/**
 * Developer — API keys for machine-to-machine integrations.
 *
 * This page lists and revokes. Issuing a key is its own page
 * (developer-new-key.tsx): choosing scopes is a security decision that wants
 * room to read, and the result is a secret shown exactly once — a dialog that
 * closes on a stray overlay click is the wrong container for that.
 *
 * Security contract: the plaintext key exists in the UI only on the screen
 * that just created it. Everywhere else a key is identified by its last 4
 * characters, and the hash never leaves the server.
 */

interface ApiKeyRow {
  id: string;
  name: string;
  keyLast4: string;
  scopes: string[];
  status: 'active' | 'revoked' | 'expired';
  rateLimitPerMinute: number | null;
  effectiveRateLimit: number;
  createdBy: { id: string; fullName: string | null } | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

function StatusBadge({ status }: { status: ApiKeyRow['status'] }) {
  if (status === 'revoked') return <Badge variant="secondary">Revoked</Badge>;
  if (status === 'expired') return <Badge variant="secondary">Expired</Badge>;
  return <Badge variant="success">Active</Badge>;
}

/** Amber inside 30 days — rotation needs a conversation with the partner, so
 *  it shouldn't be a surprise on the day it stops working. */
function ExpiryHint({ expiresAt }: { expiresAt: string | null }) {
  if (!expiresAt) return <span className="text-muted-foreground">Never</span>;
  const days = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return <span className="text-muted-foreground">{fmtDateTime(expiresAt)}</span>;
  return (
    <span className={days <= 30 ? 'text-amber-600 dark:text-amber-500' : undefined}>
      {fmtDateTime(expiresAt)}
      {days <= 30 && <span className="ml-1 text-xs">({days}d)</span>}
    </span>
  );
}

function ScopeChips({ scopes }: { scopes: string[] }) {
  if (scopes.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {scopes.map((s) => (
        <span
          key={s}
          className={`font-mono text-[10px] px-1.5 py-0.5 rounded border ${
            s.endsWith(':read')
              ? 'border-border text-muted-foreground'
              : 'border-amber-300 dark:border-amber-900 text-amber-700 dark:text-amber-500'
          }`}
        >
          {s}
        </span>
      ))}
    </span>
  );
}

export function DeveloperPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data = [], isLoading } = useQuery<ApiKeyRow[]>({
    queryKey: ['/api-keys'],
    queryFn: async () => (await api<ApiKeyRow[]>('/api-keys')).data || [],
  });

  /**
   * Revoked keys are never deleted — AuditLog rows point at them, so removing
   * one would orphan the attribution of every change it made. That means the
   * list only grows, and after a round of integration testing the handful of
   * live keys are buried among dozens of dead ones.
   *
   * Hidden by default, with the count shown so it never looks like data is
   * missing. Expired keys stay visible: unlike revoked, an expired key is
   * usually something you need to act on.
   */
  const [showRevoked, setShowRevoked] = React.useState(false);
  const revokedCount = data.filter((k) => k.status === 'revoked').length;
  const rows = showRevoked ? data : data.filter((k) => k.status !== 'revoked');
  const liveCount = data.filter((k) => k.status === 'active').length;

  /** Keys open by default — it's why you came. The request log is for
   *  investigation, so it starts closed (and doesn't fetch until opened). */
  const [keysOpen, setKeysOpen] = React.useState(true);

  /**
   * The partner playground is not served on production — Swagger's "Try it
   * out" writes to live data. Ask the server rather than guessing, so the
   * link is never offered where it would 404.
   */
  const { data: cfg } = useQuery<{ partnerDocs?: { enabled: boolean } }>({
    queryKey: ['/config'],
    queryFn: async () => (await api<any>('/config')).data || {},
    staleTime: 5 * 60 * 1000,
  });
  const docsEnabled = cfg?.partnerDocs?.enabled ?? false;

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/api-keys/${id}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => {
      toast('Revoked', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/api-keys'] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  async function confirmRevoke(k: ApiKeyRow) {
    if (await confirm({
      title: `Revoke "${k.name}"?`,
      description: 'The integration using this key loses access on its very next request. This cannot be undone — issue a new key to restore access.',
      destructive: true,
      confirmLabel: 'Revoke',
    })) revoke.mutate(k.id);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Developer</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            API keys for machine-to-machine integrations with the Atlas Fleet API.
          </p>
        </div>
        {docsEnabled ? (
          <a
            href="/api/v1/docs/integrations"
            target="_blank"
            rel="noreferrer"
            className="text-sm text-primary hover:underline inline-flex items-center gap-1"
          >
            Integration API docs <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : (
          <span className="text-xs text-muted-foreground max-w-xs text-right">
            The interactive API playground is disabled here — its
            &ldquo;Try it out&rdquo; writes to live data. Use the Sandbox environment.
          </span>
        )}
      </div>

      <Card>
        <CardHeader
          role="button"
          tabIndex={0}
          aria-expanded={keysOpen}
          onClick={() => setKeysOpen((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setKeysOpen((v) => !v); }
          }}
          className="cursor-pointer select-none group"
        >
          <div className="flex items-start gap-2">
            {keysOpen
              ? <ChevronDown className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0 group-hover:text-foreground" />
              : <ChevronRight className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0 group-hover:text-foreground" />}
            <div className="min-w-0">
              <CardTitle className="text-base">
                API Keys
                {/* Count in the header so collapsing hides the detail, not the
                    fact that there is any. */}
                {!isLoading && (
                  <span className="ml-2 font-normal text-muted-foreground">
                    {liveCount === 0 ? 'none active' : liveCount === 1 ? '1 active' : `${liveCount} active`}
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                Each key carries a fixed set of scopes and can be revoked at any time. The full key is shown
                once, when it is created — after that only its last 4 characters are kept.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        {keysOpen && (
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" asChild>
              <Link to="/admin/developer/new"><Plus className="h-4 w-4" /> New API Key</Link>
            </Button>
            {revokedCount > 0 && (
              <button
                type="button"
                onClick={() => setShowRevoked((v) => !v)}
                className="text-xs text-muted-foreground hover:text-foreground hover:underline"
              >
                {showRevoked
                  ? `Hide revoked (${revokedCount})`
                  : `Show revoked (${revokedCount})`}
              </button>
            )}
          </div>

          {isLoading && <div className="text-sm text-muted-foreground px-1 py-2">Loading…</div>}

          {!isLoading && data.length === 0 && (
            <div className="border rounded-md px-3 py-3 text-sm text-muted-foreground">
              No API keys yet. Create one to grant an integration access.
            </div>
          )}

          {/* Distinct from "no keys at all" — there ARE keys, they're just all
              revoked. Saying so avoids a hunt for data that isn't missing. */}
          {!isLoading && data.length > 0 && rows.length === 0 && (
            <div className="border rounded-md px-3 py-3 text-sm text-muted-foreground">
              No active keys. {revokedCount} revoked {revokedCount === 1 ? 'key is' : 'keys are'} hidden —
              use <span className="font-medium">Show revoked</span> above to see them.
            </div>
          )}

          {/* Cards on small screens, table on large — the dual render used across the app. */}
          {rows.length > 0 && (
            <div className="lg:hidden space-y-2">
              {rows.map((k) => (
                <div key={k.id} className="border rounded-md p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-sm truncate">{k.name}</div>
                    <StatusBadge status={k.status} />
                  </div>
                  <div className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-xs items-start">
                    <span className="text-muted-foreground">Key</span>
                    <span className="font-mono">{k.keyLast4}</span>
                    <span className="text-muted-foreground">Access</span>
                    <ScopeChips scopes={k.scopes} />
                    <span className="text-muted-foreground">Last used</span>
                    <span>{fmtDateTime(k.lastUsedAt, 'Never')}</span>
                    <span className="text-muted-foreground">Expires</span>
                    <span><ExpiryHint expiresAt={k.expiresAt} /></span>
                    <span className="text-muted-foreground">Rate limit</span>
                    <span>{k.effectiveRateLimit}/min{k.rateLimitPerMinute === null && ' (default)'}</span>
                  </div>
                  {k.status !== 'revoked' && (
                    <div className="mt-2 flex justify-end gap-3">
                      <Link to={`/admin/developer/${k.id}/edit`} className="text-primary hover:underline text-xs">
                        Edit
                      </Link>
                      <button className="text-destructive hover:underline text-xs" onClick={() => confirmRevoke(k)}>
                        Revoke
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {rows.length > 0 && (
            <div className="hidden lg:block border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Key</th>
                    <th className="text-left px-3 py-2">Access</th>
                    <th className="text-left px-3 py-2">Created by</th>
                    <th className="text-left px-3 py-2">Last used</th>
                    <th className="text-left px-3 py-2">Expires</th>
                    <th className="text-right px-3 py-2">Rate limit</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="w-28"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((k) => (
                    <tr key={k.id} className="border-t align-top">
                      <td className="px-3 py-2 font-medium">{k.name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{k.keyLast4}</td>
                      <td className="px-3 py-2"><ScopeChips scopes={k.scopes} /></td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{k.createdBy?.fullName ?? '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDateTime(k.lastUsedAt, 'Never')}</td>
                      <td className="px-3 py-2 text-xs"><ExpiryHint expiresAt={k.expiresAt} /></td>
                      <td className="px-3 py-2 text-xs text-right tabular-nums">
                        {k.effectiveRateLimit}/min
                        {k.rateLimitPerMinute === null && <span className="text-muted-foreground"> (default)</span>}
                      </td>
                      <td className="px-3 py-2 text-xs"><StatusBadge status={k.status} /></td>
                      {/* Expired keys stay editable — extending the expiry is the
                          normal way to bring one back. Revoked ones don't. */}
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {k.status !== 'revoked' && (
                          <span className="inline-flex gap-3">
                            <Link to={`/admin/developer/${k.id}/edit`} className="text-primary hover:underline text-xs">
                              Edit
                            </Link>
                            <button className="text-destructive hover:underline text-xs" onClick={() => confirmRevoke(k)}>
                              Revoke
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* `data`, not `rows`: the request-log filter must offer revoked keys
          too. "What was that key doing before we killed it" is the whole
          reason someone opens this panel. */}
      <DeveloperRequestsPanel keys={data.map((k) => ({ id: k.id, name: k.name }))} />
    </div>
  );
}

export default DeveloperPage;
